import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  AnimatePresence,
  MotionConfig,
  motion,
  useReducedMotion,
} from 'framer-motion';
import { ToastContainer } from 'react-toastify';
import Onboarding from '../OnBoardingPages/Onboarding/Onboarding';
import OnboardingTerms from '../OnBoardingPages/OnboardingTerms/OnboardingTerms';
import OnboardingFeesLimits from '../OnBoardingPages/OnboardingFeesLimits/OnboardingFeesLimits';
import OnboardingSharePhone from '../OnBoardingPages/OnboardingSharePhone/OnboardingSharePhone';
import CreatePasscode from '../PinPages/CreatePasscode/CreatePasscode';
import ConfirmPasscode from '../PinPages/ConfirmPasscode/ConfirmPasscode';
import PasscodeSet from '../PinPages/PasscodeSet/PasscodeSet';
import UnlockPasscode from '../PinPages/UnlockPasscode/UnlockPasscode';
import PostUnlockBiometricSheet, {
  POST_UNLOCK_BIOMETRIC_PROMPT_STATE_KEY,
} from '../PinPages/UnlockPasscode/PostUnlockBiometricSheet';
import NewCard from '../CardPages/NewCard/NewCard';
import CardCreated from '../CardPages/CardCreated/CardCreated';
import CardIssueStatus from '../CardPages/CardIssueStatus/CardIssueStatus';
import CardPinChange from '../CardPages/CardPinChange/CardPinChange';
import MyCard from '../CardPages/MyCard/MyCard';
import Home from '../HomePages/Home/Home';
import DepositUSDT from '../HomePages/DepositPages/DepositUSDT/DepositUSDT';
import DepositConfirming from '../HomePages/DepositPages/DepositConfirming/DepositConfirming';
import DepositSuccess from '../HomePages/DepositPages/DepositSuccess/DepositSuccess';
import DepositFailed from '../HomePages/DepositPages/DepositFailed/DepositFailed';
import History from '../HistoryPages/History/History';
import TransactionDetail from '../HistoryPages/TransactionDetail/TransactionDetail';
import ExportHistory from '../HistoryPages/ExportHistory/ExportHistory';
import ExportReady from '../HistoryPages/ExportReady/ExportReady';
import NotifInbox from '../HomePages/NotifInbox/NotifInbox';
import Profile from '../ProfilePages/Profile/Profile';
import ProfileSecurity from '../ProfilePages/ProfileSecurity/ProfileSecurity';
import ProfilePasscodeChange from '../ProfilePages/ProfilePasscodeChange/ProfilePasscodeChange';
import ProfilePersonalInfo from '../ProfilePages/ProfilePersonalInfo/ProfilePersonalInfo';
import ProfileLanguage from '../ProfilePages/ProfileLanguage/ProfileLanguage';
import ProfileHelpSupport from '../ProfilePages/ProfileHelpSupport/ProfileHelpSupport';
import Referral from '../ReferralPages/Referral/Referral';
import LegalDocumentsUpdateSheet from '../LegalDocumentsUpdateSheet/LegalDocumentsUpdateSheet';
import PageTransitionLayout from '../shared/PageTransition/PageTransitionLayout';
import BottomTabBar from '../HomePages/BottomTabBar/BottomTabBar';
import ForgotPin from '../PinPages/ForgotPin/ForgotPin';
import { getRegistration } from '../../assets/services/auth';
import {
  CurrentUserContext,
  CurrentUserProvider,
} from '../../assets/contexts/currentUserContext';
import { PostAuthRedirectContext } from '../../assets/contexts/postAuthRedirectContext';
import { TranslationProvider } from '../../assets/contexts/translationContext';
import {
  requestTelegramBiometricInfo,
  useTelegram,
} from '../../assets/hooks/useTelegram';
import { authApi } from '../../assets/api/services/authApi';
import {
  getPostLoginPath,
  getTelegramAllowsWriteToPm,
  isMissingReferralError,
  isMissingWriteAccessError,
  requestTelegramWriteAccess,
  runTelegramLoginFlow,
} from '../../assets/services/loginFlow';
import { clearUnlockSessionReady } from '../../assets/services/unlockSession';
import {
  getCards,
  isCardLimitReached,
} from '../../assets/services/cards';
import { useMainTabNavigation } from '../../assets/hooks/useMainTabNavigation';
import FullScreenSiteLoader from '../FullScreenSiteLoader/FullScreenSiteLoader';
import {
  LoginErrorScreen,
  PmPermissionScreen,
  ReferralRequiredScreen,
  SessionExpiredScreen,
  TelegramRequiredScreen,
} from '../StartupGate/StartupGate';
import { PRODUCT_NAME } from '../../assets/constants/brand';
import { API_UNAUTHORIZED_EVENT } from '../../assets/api/sessionEvents';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

const ROUTER_BASENAME = getRouterBasename();
const STARTUP_STATUS = {
  loading: 'loading',
  ready: 'ready',
  telegramRequired: 'telegramRequired',
  pmRequired: 'pmRequired',
  pmDenied: 'pmDenied',
  referralRequired: 'referralRequired',
  loginError: 'loginError',
  sessionExpired: 'sessionExpired',
};
const STARTUP_LOADER_MIN_MS = process.env.NODE_ENV === 'test' ? 0 : 3600;
const STARTUP_LOADER_FADE_MS = process.env.NODE_ENV === 'test' ? 0 : 650;
const STARTUP_CONTENT_PAINT_DELAY_MS = process.env.NODE_ENV === 'test' ? 0 : 80;
const REDIRECT_AFTER_QUERY_PARAM = 'redirectAfterTo';
const ROUTE_OVERLAY_INITIAL = {
  opacity: 1,
  transform: 'translate3d(0, 0, 0) scale(1)',
  filter: 'blur(0px)',
};
const ROUTE_OVERLAY_EXIT = {
  opacity: 0,
  transform: 'translate3d(0, 0, 0) scale(1.018)',
  filter: 'blur(10px)',
};
const ROUTE_OVERLAY_TRANSITION = {
  duration: process.env.NODE_ENV === 'test' ? 0 : 0.52,
  ease: [0.22, 1, 0.36, 1],
};
const ROUTE_TRANSITION_SKIP_PAIRS = new Set([
  '/onboarding/passcode->/onboarding/passcode/confirm',
  '/onboarding/passcode/confirm->/onboarding/passcode',
]);

function getRedirectAfterTo(search) {
  const value = new URLSearchParams(search).get(REDIRECT_AFTER_QUERY_PARAM)?.trim() || '';

  if (!value || !value.startsWith('/') || value.startsWith('//') || /[\r\n]/.test(value)) {
    return '';
  }

  return value;
}

function clearRedirectAfterToFromUrl() {
  const url = new URL(window.location.href);

  if (!url.searchParams.has(REDIRECT_AFTER_QUERY_PARAM)) {
    return;
  }

  url.searchParams.delete(REDIRECT_AFTER_QUERY_PARAM);
  window.history.replaceState(
    window.history.state,
    '',
    `${url.pathname}${url.search}${url.hash}`
  );
}

const MAIN_TAB_PAGE_PATHS = new Set(['/home', '/history', '/referral', '/profile']);

function delay(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function getRouterBasename() {
  const publicUrl = process.env.PUBLIC_URL || '';
  if (!publicUrl) {
    return undefined;
  }

  try {
    const { pathname } = new URL(publicUrl, 'http://localhost');
    return pathname.replace(/\/$/, '') || undefined;
  } catch {
    return publicUrl.replace(/\/$/, '') || undefined;
  }
}

function RootRedirect() {
  const { currentUser } = useContext(CurrentUserContext);
  const registration = getRegistration();

  if (currentUser) {
    return <Navigate to={getPostLoginPath(currentUser)} replace />;
  }

  if (registration?.passcode) {
    return <Navigate to="/unlock" replace />;
  }

  return <Navigate to="/onboarding" replace />;
}

function UnlockDestinationFade({ children }) {
  return <div className="unlock-destination">{children}</div>;
}

function NewCardRoute() {
  const location = useLocation();
  const { currentUser } = useContext(CurrentUserContext);
  const searchParams = new URLSearchParams(location.search);
  const isContinueIssue = searchParams.get('continueIssue') === '1';
  const shouldHideBack = location.state?.hideBack
    || searchParams.get('hideBack') === '1';
  const [isNewIssueBlocked] = useState(() => (
    !isContinueIssue && isCardLimitReached(getCards(), currentUser)
  ));

  if (isNewIssueBlocked) {
    return <Navigate to="/home" replace />;
  }

  return <NewCard showBack={!shouldHideBack} />;
}

function AppToasts() {
  return (
    <ToastContainer
      position="top-center"
      autoClose={3500}
      hideProgressBar
      closeButton={false}
      pauseOnFocusLoss={false}
      pauseOnHover
      theme="colored"
      toastClassName="app-toast"
    />
  );
}

function PostUnlockBiometricPrompt() {
  const location = useLocation();
  const navigate = useNavigate();
  const isOpen = location.state?.[POST_UNLOCK_BIOMETRIC_PROMPT_STATE_KEY] === true;
  const handleClose = useCallback(() => {
    const nextState = { ...(location.state || {}) };

    delete nextState[POST_UNLOCK_BIOMETRIC_PROMPT_STATE_KEY];
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      {
        replace: true,
        state: nextState,
      }
    );
  }, [location.hash, location.pathname, location.search, location.state, navigate]);

  if (!isOpen) {
    return null;
  }

  return <PostUnlockBiometricSheet onClose={handleClose} />;
}

function getRouteTransitionKey(location) {
  return `${location.pathname}${location.search}${location.hash}`;
}

function shouldSkipRouteOverlayTransition(fromLocation, toLocation) {
  if (toLocation.state?.skipRouteOverlayTransition) {
    return true;
  }

  if (
    MAIN_TAB_PAGE_PATHS.has(fromLocation.pathname)
    && MAIN_TAB_PAGE_PATHS.has(toLocation.pathname)
  ) {
    return true;
  }

  if (
    isHistoryPopupPath(fromLocation.pathname)
    || isHistoryPopupPath(toLocation.pathname)
  ) {
    return true;
  }

  return ROUTE_TRANSITION_SKIP_PAIRS.has(
    `${fromLocation.pathname}->${toLocation.pathname}`
  );
}

function isTransactionDetailPath(pathname) {
  return pathname.startsWith('/history/') && !pathname.startsWith('/history/export');
}

function isHistoryExportPath(pathname) {
  return pathname === '/history/export' || pathname === '/history/export/ready';
}

function isHistoryPopupPath(pathname) {
  return isTransactionDetailPath(pathname) || isHistoryExportPath(pathname);
}

function getHistoryPopupBackgroundLocation(location) {
  if (!isHistoryPopupPath(location.pathname)) {
    return null;
  }

  return location.state?.backgroundLocation || null;
}

const CARD_TAB_PATHS = new Set([
  '/card',
  '/card/revealed',
  '/card/frozen',
  '/card/appearance',
]);
const MAIN_TAB_IDS = new Set(['home', 'history', 'referral', 'profile']);

function getMainTabForPath(pathname) {
  if (
    pathname === '/profile/security/passcode'
    || /^\/cards\/[^/]+\/change-pin$/.test(pathname)
  ) {
    return null;
  }

  if (pathname === '/home') {
    return 'home';
  }

  if (pathname === '/history') {
    return 'history';
  }

  if (isHistoryPopupPath(pathname)) {
    return 'history';
  }

  if (pathname === '/referral') {
    return 'referral';
  }

  if (pathname === '/profile' || pathname.startsWith('/profile/')) {
    return 'profile';
  }

  if (CARD_TAB_PATHS.has(pathname)) {
    return 'card';
  }

  if (pathname.startsWith('/cards/issue/')) {
    return null;
  }

  if (
    pathname.startsWith('/cards/')
    && pathname !== '/cards/new'
    && pathname !== '/cards/created'
  ) {
    return 'card';
  }

  return null;
}

function PersistentBottomTabBar() {
  const location = useLocation();
  const visibleLocation = location.state?.backgroundLocation || location;
  const stateActiveTab = location.state?.activeTab || location.state?.bottomTab || null;
  const activeTab = MAIN_TAB_IDS.has(stateActiveTab)
    ? stateActiveTab
    : getMainTabForPath(visibleLocation.pathname);
  const handleTabChange = useMainTabNavigation();

  if (!activeTab) {
    return null;
  }

  return (
    <BottomTabBar
      activeTab={activeTab}
      onTabChange={handleTabChange}
      allowActiveTabChange={
        location.pathname === '/notifications' || location.pathname.startsWith('/profile/')
      }
    />
  );
}

function AppRouteTree({ location, isOverlay = false }) {
  return (
    <Routes location={location}>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/onboarding/terms" element={<OnboardingTerms />} />
      <Route path="/onboarding/fees-limits" element={<OnboardingFeesLimits />} />
      <Route path="/onboarding/share-phone" element={<OnboardingSharePhone />} />
      <Route path="/onboarding/passcode" element={<CreatePasscode />} />
      <Route path="/onboarding/passcode/confirm" element={<ConfirmPasscode />} />
      <Route path="/onboarding/passcode/done" element={<PasscodeSet />} />
      <Route
        path="/unlock"
        element={<UnlockPasscode disableMissingTokenRedirect={isOverlay} />}
      />
      <Route path="/forgot-pin" element={<ForgotPin />} />
      <Route
        path="/cards/new"
        element={
          <UnlockDestinationFade>
            <NewCardRoute />
          </UnlockDestinationFade>
        }
      />
      <Route path="/cards/created" element={<CardCreated />} />
      <Route path="/cards/issue/confirming" element={<CardIssueStatus mode="confirming" />} />
      <Route path="/cards/issue/submitted" element={<CardIssueStatus mode="submitted" />} />
      <Route path="/cards/:cardId/change-pin" element={<CardPinChange />} />
      <Route path="/cards/:cardId" element={<MyCard />} />
      <Route
        path="/home"
        element={
          <UnlockDestinationFade>
            <Home />
          </UnlockDestinationFade>
        }
      />
      <Route path="/notifications" element={<NotifInbox />} />
      <Route element={<PageTransitionLayout />}>
        <Route path="/deposit" element={<DepositUSDT />} />
        <Route path="/deposit/confirming" element={<DepositConfirming />} />
        <Route path="/deposit/success" element={<DepositSuccess />} />
        <Route path="/deposit/failed" element={<DepositFailed />} />
      </Route>
      <Route path="/card" element={<MyCard />} />
      <Route path="/card/revealed" element={<Navigate to="/card" replace />} />
      <Route path="/card/frozen" element={<Navigate to="/card" replace />} />
      <Route path="/card/appearance" element={<Navigate to="/card" replace />} />
      <Route element={<PageTransitionLayout />}>
        <Route path="/history" element={<History />} />
        <Route path="/history/:id" element={<TransactionDetail />} />
      </Route>
      <Route element={<PageTransitionLayout />}>
        <Route path="/referral" element={<Referral />} />
      </Route>
      <Route element={<PageTransitionLayout />}>
        <Route path="/profile" element={<Profile />} />
        <Route path="/profile/personal-information" element={<ProfilePersonalInfo />} />
        <Route path="/profile/security" element={<ProfileSecurity />} />
        <Route path="/profile/security/passcode" element={<ProfilePasscodeChange />} />
        <Route path="/profile/language" element={<ProfileLanguage />} />
        <Route path="/profile/help" element={<ProfileHelpSupport />} />
        <Route path="/profile/invite" element={<Navigate to="/referral" replace />} />
      </Route>
    </Routes>
  );
}

function AppRouteTransition() {
  const location = useLocation();
  const shouldReduceMotion = useReducedMotion();
  const modalBackgroundLocation = getHistoryPopupBackgroundLocation(location);
  const routeLocation = modalBackgroundLocation || location;
  const isHistoryPopupOpen = Boolean(modalBackgroundLocation);
  const locationKey = getRouteTransitionKey(location);
  const previousLocationRef = useRef(location);
  const previousLocationKeyRef = useRef(locationKey);
  const [overlayLocation, setOverlayLocation] = useState(null);

  useLayoutEffect(() => {
    if (previousLocationKeyRef.current === locationKey) {
      previousLocationRef.current = location;
      return;
    }

    if (
      shouldReduceMotion
      || shouldSkipRouteOverlayTransition(previousLocationRef.current, location)
    ) {
      setOverlayLocation(null);
    } else {
      setOverlayLocation(previousLocationRef.current);
    }

    previousLocationRef.current = location;
    previousLocationKeyRef.current = locationKey;
  }, [location, locationKey, shouldReduceMotion]);

  return (
    <div className="app-route-transition">
      <div className="app-route-transition__current">
        <AppRouteTree location={routeLocation} />
      </div>
      {overlayLocation && (
        <motion.div
          key={getRouteTransitionKey(overlayLocation)}
          className="app-route-transition__overlay"
          initial={ROUTE_OVERLAY_INITIAL}
          animate={ROUTE_OVERLAY_EXIT}
          transition={ROUTE_OVERLAY_TRANSITION}
          onAnimationComplete={() => setOverlayLocation(null)}
        >
          <AppRouteTree location={overlayLocation} isOverlay />
        </motion.div>
      )}
      <AnimatePresence>
        {isHistoryPopupOpen && (
          <Routes location={location}>
            <Route path="/history/export" element={<ExportHistory />} />
            <Route path="/history/export/ready" element={<ExportReady />} />
            <Route path="/history/:id" element={<TransactionDetail />} />
          </Routes>
        )}
      </AnimatePresence>
    </div>
  );
}

function AppRoutes({ telegram }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { setCurrentUser } = useContext(CurrentUserContext);
  const didStartRef = useRef(false);
  const didSessionExpireRef = useRef(false);
  const isMountedRef = useRef(true);
  const loaderStartedAtRef = useRef(Date.now());
  const [startupStatus, setStartupStatus] = useState(STARTUP_STATUS.loading);
  const [isLoaderVisible, setIsLoaderVisible] = useState(true);
  const [isLoaderExiting, setIsLoaderExiting] = useState(false);
  const [isRequestingPm, setIsRequestingPm] = useState(false);
  const [redirectAfterTo, setRedirectAfterTo] = useState(() => (
    getRedirectAfterTo(location.search)
  ));

  useEffect(() => {
    const nextRedirectAfterTo = getRedirectAfterTo(location.search);

    if (!nextRedirectAfterTo) {
      return;
    }

    setRedirectAfterTo(nextRedirectAfterTo);
    clearRedirectAfterToFromUrl();
  }, [location.search]);

  const consumePostAuthRedirect = useCallback(
    (defaultPath) => {
      if (defaultPath !== '/home' || !redirectAfterTo) {
        return defaultPath;
      }

      setRedirectAfterTo('');
      return redirectAfterTo;
    },
    [redirectAfterTo]
  );

  const postAuthRedirectValue = useMemo(
    () => ({
      redirectAfterTo,
      consumePostAuthRedirect,
    }),
    [consumePostAuthRedirect, redirectAfterTo]
  );

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const handleApiUnauthorized = () => {
      didStartRef.current = true;
      didSessionExpireRef.current = true;
      setCurrentUser(null);
      setIsLoaderVisible(false);
      setIsLoaderExiting(false);
      setIsRequestingPm(false);
      setStartupStatus(STARTUP_STATUS.sessionExpired);
    };

    window.addEventListener(API_UNAUTHORIZED_EVENT, handleApiUnauthorized);

    return () => {
      window.removeEventListener(API_UNAUTHORIZED_EVENT, handleApiUnauthorized);
    };
  }, [setCurrentUser]);

  const startLoader = useCallback(() => {
    clearUnlockSessionReady();
    didSessionExpireRef.current = false;
    loaderStartedAtRef.current = Date.now();
    setIsLoaderVisible(true);
    setIsLoaderExiting(false);
    setStartupStatus(STARTUP_STATUS.loading);
  }, []);

  const finishLoader = useCallback(async ({
    nextStatus,
    currentUser,
    path,
  }) => {
    if (
      didSessionExpireRef.current
      && nextStatus !== STARTUP_STATUS.sessionExpired
    ) {
      return;
    }

    const elapsed = Date.now() - loaderStartedAtRef.current;
    const remaining = Math.max(STARTUP_LOADER_MIN_MS - elapsed, 0);

    if (remaining > 0) {
      await delay(remaining);
    }

    if (
      !isMountedRef.current
      || (
        didSessionExpireRef.current
        && nextStatus !== STARTUP_STATUS.sessionExpired
      )
    ) {
      return;
    }

    if (currentUser !== undefined) {
      setCurrentUser(currentUser);
    }

    if (path) {
      navigate(consumePostAuthRedirect(path), { replace: true });
    }

    setStartupStatus(nextStatus);

    if (STARTUP_CONTENT_PAINT_DELAY_MS > 0) {
      await delay(STARTUP_CONTENT_PAINT_DELAY_MS);
    }

    if (
      !isMountedRef.current
      || (
        didSessionExpireRef.current
        && nextStatus !== STARTUP_STATUS.sessionExpired
      )
    ) {
      return;
    }

    setIsLoaderExiting(true);

    if (STARTUP_LOADER_FADE_MS > 0) {
      await delay(STARTUP_LOADER_FADE_MS);
    }

    if (
      !isMountedRef.current
      || (
        didSessionExpireRef.current
        && nextStatus !== STARTUP_STATUS.sessionExpired
      )
    ) {
      return;
    }

    setIsLoaderVisible(false);
    setIsLoaderExiting(false);
  }, [consumePostAuthRedirect, navigate, setCurrentUser]);

  const runLogin = useCallback(async () => {
    if (!telegram.initData) {
      await finishLoader({ nextStatus: STARTUP_STATUS.telegramRequired });
      return;
    }

    startLoader();

    try {
      let biometricDeviceId = telegram.biometric?.deviceId || '';

      try {
        const latestBiometric = await requestTelegramBiometricInfo(telegram.webApp);
        biometricDeviceId = latestBiometric?.deviceId || biometricDeviceId;
      } catch {
        // Login should not depend on optional biometry metadata.
      }

      const { currentUser, path } = await runTelegramLoginFlow({
        authService: authApi,
        deviceId: biometricDeviceId || undefined,
        initData: telegram.initData,
        theme: telegram.colorScheme || 'light',
        webApp: telegram.webApp,
      });

      if (didSessionExpireRef.current) {
        return;
      }

      await finishLoader({
        nextStatus: STARTUP_STATUS.ready,
        currentUser,
        path,
      });
    } catch (error) {
      if (didSessionExpireRef.current) {
        return;
      }

      if (isMissingReferralError(error)) {
        await finishLoader({ nextStatus: STARTUP_STATUS.referralRequired });
        return;
      }

      if (isMissingWriteAccessError(error)) {
        await finishLoader({ nextStatus: STARTUP_STATUS.pmRequired });
        return;
      }

      await finishLoader({ nextStatus: STARTUP_STATUS.loginError });
    }
  }, [
    finishLoader,
    startLoader,
    telegram.biometric?.deviceId,
    telegram.colorScheme,
    telegram.initData,
    telegram.webApp,
  ]);

  useEffect(() => {
    if (!telegram.isReady) {
      return;
    }

    if (didStartRef.current) {
      return;
    }

    if (!telegram.initData) {
      didStartRef.current = true;
      finishLoader({ nextStatus: STARTUP_STATUS.telegramRequired });
      return;
    }

    didStartRef.current = true;

    const allowsWriteToPm = getTelegramAllowsWriteToPm({
      webApp: telegram.webApp,
      initData: telegram.initData,
    });

    if (!allowsWriteToPm) {
      finishLoader({ nextStatus: STARTUP_STATUS.pmRequired });
      return;
    }

    runLogin();
  }, [finishLoader, runLogin, telegram.initData, telegram.isReady, telegram.webApp]);

  const handleRequestPm = useCallback(async () => {
    setIsRequestingPm(true);

    try {
      const isAllowed = await requestTelegramWriteAccess(telegram.webApp);

      if (isAllowed) {
        await runLogin();
        return;
      }

      setStartupStatus(STARTUP_STATUS.pmDenied);
    } finally {
      setIsRequestingPm(false);
    }
  }, [runLogin, telegram.webApp]);

  const handleRetryLogin = useCallback(() => {
    runLogin();
  }, [runLogin]);

  return (
    <PostAuthRedirectContext.Provider value={postAuthRedirectValue}>
      <div className="app">
        <AppToasts />
        {isLoaderVisible && (
          <FullScreenSiteLoader
            isExiting={isLoaderExiting}
          />
        )}
        {startupStatus === STARTUP_STATUS.telegramRequired && <TelegramRequiredScreen />}
        {startupStatus === STARTUP_STATUS.pmRequired && (
          <PmPermissionScreen
            isRequesting={isRequestingPm}
            onRequest={handleRequestPm}
          />
        )}
        {startupStatus === STARTUP_STATUS.pmDenied && (
          <PmPermissionScreen
            denied
            isRequesting={isRequestingPm}
            onRequest={handleRequestPm}
          />
        )}
        {startupStatus === STARTUP_STATUS.referralRequired && <ReferralRequiredScreen />}
        {startupStatus === STARTUP_STATUS.loginError && (
          <LoginErrorScreen
            isRetrying={startupStatus === STARTUP_STATUS.loading}
            onRetry={handleRetryLogin}
          />
        )}
        {startupStatus === STARTUP_STATUS.sessionExpired && <SessionExpiredScreen />}
        {startupStatus === STARTUP_STATUS.ready && (
          <>
            <AppRouteTransition />
            <PersistentBottomTabBar />
            <LegalDocumentsUpdateSheet />
            <PostUnlockBiometricPrompt />
          </>
        )}
      </div>
    </PostAuthRedirectContext.Provider>
  );
}

function App() {
  const telegram = useTelegram();

  useEffect(() => {
    document.title = PRODUCT_NAME;
  }, []);

  return (
    <MotionConfig reducedMotion="user">
      <CurrentUserProvider>
        <TranslationProvider telegramUser={telegram.user}>
          <BrowserRouter
            basename={ROUTER_BASENAME}
            future={{ v7_relativeSplatPath: true, v7_startTransition: true }}
          >
            <AppRoutes telegram={telegram} />
          </BrowserRouter>
        </TranslationProvider>
      </CurrentUserProvider>
    </MotionConfig>
  );
}

export default App;
